import {
  Button,
  Divider,
  Flex,
  Link,
  Skeleton,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { toZonedTime } from "date-fns-tz";
import { RiCheckLine, RiShareBoxLine } from "react-icons/ri";
import { StatusTag } from "src/components/DataDisplay/StatusTag";
import { Card } from "src/components/Layout/Card";
import { Glossary } from "src/components/Text/Glossary";
import { formatTimeWithTimeZone } from "src/scenes/orgAdmin/forms/export/dateUtils";
import { formatIsoDate, formatMoney } from "src/services/format";
import { isNonEmptyString, isNotNull } from "src/services/predicates";

export interface ReceiptContentData {
  id: string;
  name: string;
  amountInCents: number;
  currency: string;
  paymentStatus: string;
  paidAt: string | null;
  studentName: string;
  form: {
    formTemplateName: string;
    enrollmentPeriodName: string;
    gradeName?: string | null;
  } | null;
  receiptUrl: string | null;
}

const PaidTag = () => (
  <StatusTag
    status={{
      label: "Paid",
      longLabel: "Paid",
      colorScheme: "green",
      icon: RiCheckLine,
    }}
  />
);

interface ReceiptContentProps {
  data: ReceiptContentData;
}

export const ReceiptContent: React.FC<ReceiptContentProps> = ({ data }) => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const paidAt = data.paidAt ? toZonedTime(data.paidAt, tz) : null;
  const shortId = `${data.id.slice(0, 4)} ${data.id.slice(4, 8)}`;

  return (
    <Card showShadow useFlex gap="4" fontSize="sm">
      <Flex direction="column" alignItems="center" padding="2">
        {data.paymentStatus === "paid" ? <PaidTag /> : null}
        <Text fontWeight="semibold">{data.name}</Text>
        <Text fontSize="2xl" fontWeight="semibold">
          {formatMoney(data.amountInCents, data.currency)}
        </Text>
        {isNotNull(paidAt) && isNotNull(data.paidAt) ? (
          <Text fontSize="xs">
            {formatIsoDate(data.paidAt, "MMMM dd, yyyy", tz)} ·{" "}
            {formatTimeWithTimeZone(paidAt, tz, true)}
          </Text>
        ) : null}
      </Flex>
      <Divider />
      <Flex direction="column" padding="2" gap="6">
        <Flex direction="column" gap="2">
          <Flex justifyContent="space-between">
            <Text>Item</Text>
            <Text>{data.name}</Text>
          </Flex>
          {isNotNull(data.form) ? (
            <>
              <Flex justifyContent="space-between">
                <Text>Enrollment period</Text>
                <Text>{data.form.enrollmentPeriodName}</Text>
              </Flex>
              <Flex justifyContent="space-between">
                <Text>Form</Text>
                <Text>{data.form.formTemplateName}</Text>
              </Flex>
              {isNonEmptyString(data.form.gradeName) ? (
                <Flex justifyContent="space-between">
                  <Text>Grade</Text>
                  <Text>{data.form.gradeName}</Text>
                </Flex>
              ) : null}
            </>
          ) : null}
          <Flex justifyContent="space-between">
            <Glossary>Student</Glossary>
            <Text>{data.studentName}</Text>
          </Flex>
          <Flex justifyContent="space-between">
            <Text>Payment ID</Text>
            <Tooltip label={data.id}>
              <Text>{shortId}</Text>
            </Tooltip>
          </Flex>
        </Flex>
        <Flex direction="column" gap="2">
          {isNotNull(data.receiptUrl) ? (
            <Button
              leftIcon={<RiShareBoxLine />}
              variant="outline"
              colorScheme="gray"
              as={Link}
              href={data.receiptUrl}
              target="_blank"
            >
              View receipt
            </Button>
          ) : null}
        </Flex>
      </Flex>
    </Card>
  );
};

export const ReceiptContentLoading = () => {
  return (
    <Card showShadow gap="4" useFlex>
      <Flex direction="column" alignItems="center" padding="2" gap="2">
        <Skeleton width="100px" height="1rem" />
        <Skeleton width="150px" height="1rem" />
        <Skeleton width="150px" height="2rem" />
        <Skeleton width="170px" height="1rem" />
      </Flex>
      <Divider />
      <Flex direction="column" alignItems="center" padding="2" gap="2">
        <Skeleton width="100%" height="1rem" />
        <Skeleton width="100%" height="1rem" />
        <Skeleton width="100%" height="1rem" />
      </Flex>
    </Card>
  );
};
